import { useCallback, useEffect, useMemo, useState } from "react";

import { useGetAttributeValues } from "@/api/dataset/useDatasetAPI";
import {
  DateRange,
  DateTimeRange,
  MinMaxRange,
  TimeRange
} from "@/components/dataset/datasetAttributeFilter/types";
import { COUNTRY_NAME } from "@/constants/dataset";
import { Timezone } from "@/constants/dateTime";
import {
  createAdminAreaOption,
  generateAttributeFilterOptions,
  getAvailAggregationTypeAdminArea,
  getFilteredSavedFilters,
  handleAdminAreaChangeHelper,
  isAdminAreaValid,
  isDateTimeRangeValid
} from "@/helpers/datasetAttributeHelper";
import {
  ADMIN_AREA_LEVEL_MAP,
  AdminAreaLevelKey,
  EAdminAreaLevel
} from "@/types/adminArea";
import {
  AttributeOption,
  DatasetDetailResponse,
  DatasetFilter,
  EFilterOptionType,
  FilterOption
} from "@/types/dataset";
import { AdminAreaSettings } from "@/types/spatial";

type Props = {
  timezone: Timezone;
  selectedDataset: DatasetDetailResponse | null;
  initialDatasetFilters?: DatasetFilter[];
};

export const useAttributeFilter = ({
  timezone,
  selectedDataset,
  initialDatasetFilters = []
}: Props) => {
  const [selectedFilter, setSelectedFilter] = useState<AttributeOption | null>(
    null
  );
  const [selectedEditFilter, setSelectedEditFilter] =
    useState<AttributeOption | null>(null);
  const [minMaxRange, setMinMaxRange] =
    useState<DatasetFilter["minMaxRange"]>();
  const [dateTimeRange, setDateTimeRange] =
    useState<DatasetFilter["dateTimeRange"]>();
  const [dateRange, setDateRange] = useState<DatasetFilter["dateRange"]>();
  const [timeRange, setTimeRange] = useState<DatasetFilter["timeRange"]>();
  const [isEditMode, setIsEditMode] = useState(false);
  const [defaultSelectedValue, setDefaultSelectedValue] =
    useState<AttributeOption | null>(null);
  const [filterOptions, setFilterOptions] = useState<AttributeOption[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedAdminArea, setSelectedAdminArea] =
    useState<AdminAreaSettings | null>(null);
  const [showErrorPOI, setShowErrorPOI] = useState(false);

  const initialSavedFilter = useMemo(() => {
    const data = getFilteredSavedFilters(
      initialDatasetFilters,
      selectedDataset,
      true
    );

    if (!data.length) return initialDatasetFilters;

    const hasUndefinedAttributeCode = data.some((item) => !item.attributeCode);
    if (!hasUndefinedAttributeCode) return initialDatasetFilters;

    const selectedAdmArea = data[0]?.selectedOptions || [];
    if (selectedAdmArea.length === 0) return initialDatasetFilters;

    const lastCode = selectedAdmArea.at(-1)?.split("::")[1] || "";

    const levelConfigs = [
      EAdminAreaLevel.LEVEL_1,
      EAdminAreaLevel.LEVEL_2,
      EAdminAreaLevel.LEVEL_3,
      EAdminAreaLevel.LEVEL_4
    ].map((attributeCode, index) => ({
      level: {
        id: selectedAdmArea[index]?.split("::")[0] || "",
        code: selectedAdmArea[index]?.split("::")[1] || ""
      },
      attributeCode
    }));

    const datasetFiltersNew: DatasetFilter[] = levelConfigs
      .filter((config) => config.level.code)
      .map((config) => ({
        filterId: `${data[0]?.attributeId ?? ""}`,
        filterLabel: `${data[0]?.filterLabel ?? ""}`,
        attributeCode: config.attributeCode,
        attributeId: data[0]?.attributeId ?? "",
        paired_key_attribute_id: data[0]?.paired_key_attribute_id ?? null,
        admAreaUnit: `${data[0]?.admAreaUnit ?? ""}`,
        admAreaId: config.level.id,
        admAreaAllSelected: data[0]?.selectedOptions ?? [],
        filterType: EFilterOptionType.MULTIPLE,
        selectedOptions: [lastCode]
      }));

    return [
      ...datasetFiltersNew,
      ...initialDatasetFilters.filter(
        (filter) => filter.attributeId !== data[0]?.attributeId
      )
    ];
  }, [initialDatasetFilters, selectedDataset]);

  const [savedFilters, setSavedFilters] =
    useState<DatasetFilter[]>(initialSavedFilter);

  // Determine filter types
  const [
    isMultipleSelection,
    isDateTimeFilter,
    isDateFilter,
    isTimeFilter,
    isMinMaxFilter
  ] = useMemo(
    () => [
      selectedFilter?.type === EFilterOptionType.MULTIPLE,
      selectedFilter?.type === EFilterOptionType.DATE_TIME,
      selectedFilter?.type === EFilterOptionType.DATE,
      selectedFilter?.type === EFilterOptionType.TIME,
      selectedFilter?.type === EFilterOptionType.MIN_MAX
    ],
    [selectedFilter]
  );

  // Fetch attribute values
  const { data: attributeValues = [], isLoading: isGettingAttributeValues } =
    useGetAttributeValues({
      savedFilters,
      timezone,
      datasetId: selectedDataset?.id,
      selectedFilter,
      selectedEditFilter,
      isMultipleSelection,
      isEditMode
    });

  const getFilterType = (): DatasetFilter["filterType"] => {
    if (isMultipleSelection) return EFilterOptionType.MULTIPLE;
    if (isDateTimeFilter) return EFilterOptionType.DATE_TIME;
    if (isDateFilter) return EFilterOptionType.DATE;
    if (isTimeFilter) return EFilterOptionType.TIME;
    return EFilterOptionType.MIN_MAX;
  };

  const resetSelectedFilter = () => {
    setIsEditMode(false);
    setSelectedFilter(null);
    setSelectedOptions([]);
    setDefaultSelectedValue(null);
    setSelectedEditFilter(null);
  };

  const resetAttributeFilter = () => {
    setDateTimeRange(undefined);
    setMinMaxRange(undefined);
    setDateRange(undefined);
    setTimeRange(undefined);
    setSearchTerm("");
  };

  const handleAddAttributeFilter = () => {
    setShowFilters(true);
    resetSelectedFilter();
    resetAttributeFilter();
    setShowErrorPOI(false);
  };

  const handleBack = () => {
    setShowFilters(false);
    resetSelectedFilter();
    resetAttributeFilter();
  };

  const lv0Option = useMemo(() => {
    const selectedCountry = selectedDataset?.country_id;
    const isValidCountry = selectedCountry && selectedCountry !== "global";
    return isValidCountry
      ? { label: COUNTRY_NAME[selectedCountry], value: selectedCountry }
      : null;
  }, [selectedDataset]);

  const initialAdminArea: AdminAreaSettings = useMemo(() => {
    const levelKeys: AdminAreaLevelKey[] = ["lv1", "lv2", "lv3", "lv4"];

    const adminAreaFilters = levelKeys.reduce<Partial<AdminAreaSettings>>(
      (acc, levelKey) => {
        const attributeCode = ADMIN_AREA_LEVEL_MAP[levelKey];
        const filter = savedFilters.find(
          (f) => f.attributeCode === attributeCode
        );
        acc[levelKey] = createAdminAreaOption(filter);
        return acc;
      },
      {}
    );
    return {
      lv0: lv0Option ? { ...lv0Option, isMatched: false } : null,
      ...adminAreaFilters
    };
  }, [lv0Option, savedFilters]);

  const adminAreaValid = useMemo(
    () =>
      isAdminAreaValid(selectedAdminArea || initialAdminArea, selectedDataset),
    [initialAdminArea, selectedAdminArea, selectedDataset]
  );

  const isAdminAreaSelection =
    ["adm_area", "poi", "H3"].includes(
      selectedDataset?.spatial_aggregation_type ?? ""
    ) && selectedDataset?.source === "bvt";

  const isAdminAreaSelectionValid = useMemo(
    // When on admin area selection, it will check adminAreaValid
    // When not on admin area selection, it will return true
    () => (isAdminAreaSelection ? adminAreaValid : true),
    [isAdminAreaSelection, adminAreaValid]
  );

  const getAvailableFilterOptions = useCallback(
    () =>
      getAvailAggregationTypeAdminArea(
        filterOptions,
        savedFilters,
        selectedDataset
      ),
    [filterOptions, savedFilters, selectedDataset]
  );

  const isAttributeFilterValid = useMemo(() => {
    if (isMultipleSelection) return selectedOptions.length !== 0;
    if (isDateTimeFilter) return isDateTimeRangeValid(dateTimeRange);
    if (isDateFilter) return !!dateRange?.from || !!dateRange?.to;
    if (isTimeFilter) return !!timeRange?.from || !!timeRange?.to;
    if (isMinMaxFilter) return !!minMaxRange?.min || !!minMaxRange?.max;
    return false;
  }, [
    isMultipleSelection,
    selectedOptions,
    isDateTimeFilter,
    dateTimeRange,
    isDateFilter,
    dateRange,
    isTimeFilter,
    timeRange,
    isMinMaxFilter,
    minMaxRange
  ]);

  const handleEditFilter = (filter: DatasetFilter) => {
    const filterOption = filterOptions.find(
      (opt) =>
        opt.id === filter.filterId &&
        opt.attribute_code === filter.attributeCode
    );
    if (filterOption) {
      setSelectedEditFilter(filterOption);
      setSelectedFilter(filterOption);
      setShowFilters(true);
      setIsEditMode(true);
      setDefaultSelectedValue(filterOption);
      setShowErrorPOI(false);
    }

    if (filter.dateTimeRange) {
      setDateTimeRange(filter.dateTimeRange);
    } else if (filter.dateRange) {
      setDateRange(filter.dateRange);
    } else if (filter.timeRange) {
      setTimeRange(filter.timeRange);
    } else if (filter.minMaxRange) {
      setMinMaxRange(filter.minMaxRange);
    } else {
      setSelectedOptions(filter.selectedOptions || []);
    }
  };

  const handleDeleteFilter = (filter: DatasetFilter) => {
    const newFilter = savedFilters.filter(
      (f) =>
        f.filterId !== filter.filterId ||
        f.attributeCode !== filter.attributeCode
    );
    setSavedFilters(newFilter);
    return newFilter;
  };

  const selectAttributeItem = useMemo(
    () =>
      getAvailableFilterOptions().find(
        (item) =>
          item.id === selectedFilter?.id &&
          item.attribute_code === selectedFilter?.attribute_code
      ),
    [selectedFilter, getAvailableFilterOptions]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSelectOption = (optionId: string) => {
    setSelectedOptions((prev) => {
      if (prev.includes(optionId)) {
        return prev.filter((id) => id !== optionId);
      }
      return [...prev, optionId];
    });
  };

  const handleSelectAll = (data: FilterOption[]) => {
    if (selectedOptions.length === data.length) {
      setSelectedOptions([]);
    } else {
      setSelectedOptions(
        data
          ?.map((opt) => opt.value)
          ?.filter((value): value is string => value !== undefined)
      );
    }
  };

  const handleSelectFilter = (option: AttributeOption | null) => {
    if (!option || option?.disabled) return;
    setSelectedFilter(option);
    setDateTimeRange(undefined);
    setSelectedOptions([]);
    setSearchTerm("");
    setDateRange(undefined);
    setTimeRange(undefined);
    setMinMaxRange(undefined);
  };

  const handleAdminAreaChange = (newAdminArea: AdminAreaSettings | null) => {
    const newFilter = handleAdminAreaChangeHelper(
      newAdminArea,
      filterOptions,
      savedFilters
    );

    // Auto-select the first available filter option that is not disabled
    const firstAvailableOption = getAvailableFilterOptions().find(
      (option) => !option.disabled
    );

    setSavedFilters(newFilter);

    if (firstAvailableOption && !firstAvailableOption.disabled) {
      setDefaultSelectedValue(firstAvailableOption);
    } else {
      setSelectedFilter(null);
      setDefaultSelectedValue(null);
    }

    // Reset attribute filter states
    resetAttributeFilter();
  };

  const handleSaveFilter = async () => {
    const selectedFilterOption = filterOptions.find(
      (opt) =>
        opt.id === selectedFilter?.id &&
        opt.attribute_code === selectedFilter?.attribute_code
    );
    if (!selectedFilterOption) return;
    const {
      id,
      label,
      value,
      attribute_code,
      time_format,
      paired_key_attribute_id
    } = selectedFilterOption;
    const filterType = getFilterType();
    const newFilter: DatasetFilter = {
      filterId: id,
      filterLabel: label,
      attributeCode: attribute_code,
      attributeId: value,
      filterType: filterType,
      time_format: time_format ?? null,
      paired_key_attribute_id: paired_key_attribute_id ?? null,
      ...(isMultipleSelection ? { selectedOptions } : {}),
      ...(isDateTimeFilter && dateTimeRange ? { dateTimeRange } : {}),
      ...(isDateFilter && dateRange ? { dateRange } : {}),
      ...(isTimeFilter && timeRange ? { timeRange } : {}),
      ...(isMinMaxFilter && minMaxRange ? { minMaxRange } : {})
    };

    const updatedFilters = (prev: DatasetFilter[]) => {
      if (isEditMode) {
        return prev.map((filter) =>
          filter.filterId === selectedEditFilter?.id &&
          filter.attributeCode === selectedEditFilter?.attribute_code
            ? newFilter
            : filter
        );
      }
      return [...prev, newFilter];
    };

    if (selectedFilter) setSavedFilters(updatedFilters);

    handleBack();
    return updatedFilters(savedFilters);
  };

  const onFilterChange = (
    data:
      | FilterOption[]
      | DateTimeRange
      | DateRange
      | TimeRange
      | MinMaxRange
      | string
  ) => {
    if (isMultipleSelection) {
      if (typeof data === "string") {
        handleSelectOption(data);
      } else if (typeof data === "object" && Array.isArray(data)) {
        handleSelectAll(data);
      }
    } else if (isDateTimeFilter) {
      setDateTimeRange(data as DateTimeRange);
    } else if (isDateFilter) {
      setDateRange(data as DateRange);
    } else if (isTimeFilter) {
      setTimeRange(data as TimeRange);
    } else if (isMinMaxFilter) {
      setMinMaxRange(data as MinMaxRange);
    }
  };

  useEffect(() => {
    setFilterOptions(generateAttributeFilterOptions(selectedDataset));
  }, [selectedDataset]);

  return {
    attributeValues,
    isGettingAttributeValues,
    selectedFilter,
    selectedEditFilter,
    defaultSelectedValue,
    filterOptions,
    savedFilters,
    setSavedFilters,
    showFilters,
    setShowFilters,
    getAvailableFilterOptions,
    initialAdminArea,
    isAttributeFilterValid,
    selectAttributeItem,
    searchTerm,
    selectedOptions,
    handleBack,
    handleAddAttributeFilter,
    handleSaveFilter,
    handleEditFilter,
    handleDeleteFilter,
    handleSearchChange,
    handleSelectFilter,
    handleAdminAreaChange,
    onFilterChange,
    selectedAdminArea,
    setSelectedAdminArea,
    adminAreaValid,
    isAdminAreaSelection,
    isAdminAreaSelectionValid,
    showErrorPOI,
    setShowErrorPOI
  };
};
